import { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { MESSAGE_TYPE } from 'shared/constants/messages';

// The server decides: `requesterView` is the show payload's `requester_view`,
// true only when access came through the ParallelRequesterAccess carve-out —
// that is, when inbox membership did NOT let the user in. Membership therefore
// wins over having opened the conversation: someone on the team that answers
// the queue works their own item in full, even one they requested themselves,
// which is what the API already lets them do.
//
// The route rule below is the fallback for the window before the show payload
// lands (list rows carry no requester_view) and for surfaces that never fetch
// it. It errs toward the trimmed view, so a real requester never sees controls
// that would 403 on click.
export const isParallelRequesterView = ({
  channelType,
  conversationInboxId,
  routeInboxId,
  parallelChannel,
  requesterView,
}) => {
  if (typeof requesterView === 'boolean') return requesterView;
  // Email children are normal conversations for everyone inside: the
  // "requester" talks to an external recipient over email, so no internal
  // viewer gets the trimmed view. An absent parallelChannel (internal
  // children, payloads predating the field) keeps the route-based rule below.
  if (parallelChannel === 'email') return false;
  if (channelType !== INBOX_TYPES.PARALLEL_CONVERSATION) return false;
  if (!conversationInboxId) return false;
  // Route params arrive as strings while the conversation carries a numeric
  // inbox_id; an absent route param yields NaN, which never matches.
  return Number(routeInboxId) !== Number(conversationInboxId);
};

// Which side of the thread a bubble renders on, and therefore which styling it
// gets ('right' is the "my message" treatment). In the requester view the
// thread flips: the requester's own messages are stored as incoming (they are
// the parallel inbox's "customer") but must read as mine — right — while the
// attendant's outgoing messages go left as the interlocutor. Presentation
// only: message_type semantics (email heads, reply-to, tracking) stay real.
export const getBubbleAlignment = ({
  messageType,
  isParallelRequesterView: inRequesterView = false,
}) => {
  if (messageType === MESSAGE_TYPE.ACTIVITY) return 'center';
  const isRight =
    messageType === MESSAGE_TYPE.OUTGOING ||
    messageType === MESSAGE_TYPE.TEMPLATE;
  // message_type alone decides the side. Never key on echo_id: broadcasts
  // carry the SENDER's echo_id to every viewer, so treating "has echo_id" as
  // "mine" flips the attendant's live messages onto the requester's side.
  // The optimistic pending bubble is safe without it — createPendingMessage
  // builds it as incoming for requester-view sends.
  if (!inRequesterView) return isRight ? 'right' : 'left';
  return isRight ? 'left' : 'right';
};

// The account's DEFAULT parallel inbox: server-provisioned plumbing, not a
// queue anyone attends. It only exists to host the email children opened
// without an explicit destination, and its `email_outbound_inbox` IS the
// account-level email transport setting (edited in Account Settings). It is
// therefore hidden from every inbox list and picker — the `inboxes/getInboxes`
// getter filters it out, so screens inherit the rule instead of repeating it.
// Team parallel inboxes are ordinary inboxes and stay visible.
export const isDefaultParallelInbox = inbox =>
  inbox?.channel_type === INBOX_TYPES.PARALLEL_CONVERSATION &&
  Boolean(inbox?.parallel_default);

// The account's default parallel inbox stores one canonical name server-side
// ('Conversas Paralelas Externas') but must read localized wherever the full inbox
// object is available — it is account plumbing, not a team someone named.
// Surfaces that only get the API name string (accordion inbox_name) keep the
// canonical name; that is acceptable and deliberate.
export const parallelInboxDisplayName = (inbox, t) => {
  if (!inbox) return '';
  if (
    inbox.parallel_default &&
    inbox.channel_type === INBOX_TYPES.PARALLEL_CONVERSATION
  ) {
    return t('PARALLEL_CONVERSATIONS.DEFAULT_INBOX_NAME');
  }
  return inbox.name;
};

// The requester never sees the conversation's activity timeline — those events
// (assignments, status moves, agentic telemetry) belong to whoever attends the
// parallel conversation. The backend filters the messages index; this covers
// activities that reach the client anyway (cable pushes, cached state).
export const filterOutActivityMessages = messages =>
  (messages || []).filter(
    message => message.message_type !== MESSAGE_TYPE.ACTIVITY
  );
